import { FqlUtilities } from '@frankai/fql'

import { flattenTop } from '@/admin-collection-layout/composables/fqlSearchHelpers.js'
import { enumMetaFor } from '@/lib/enumMeta.js'

/**
 * What the two identity screens know about their records: the fields the layout may search
 * and show, what each reads as, and how a typed query becomes the identity server's own list
 * parameters.
 *
 * The administration nouns read all of this from the server's searchable surface. The identity
 * server publishes none — its lists take a person's `q` and a program's `status`, and nothing
 * else — so the surface is written here, from the shapes `Administration.routes.mjs` answers.
 *
 * **The search bar speaks FQL; the identity server does not.** A bare word is an FQL error
 * ("bare terms are not executable"), so a person is found with a clause — `name:dana*`,
 * `primaryEmail:dana@` — and the clause's text becomes the server's `q`. The server answers
 * every record the text appears in; the layout's own engine then narrows the page to exactly
 * what the clause asked (a prefix for `dana*`, the exact value for `dana`), as it does for every
 * noun. A program's `status:` clause is sent as the server's filter; every other clause narrows
 * the page the server answered.
 */

/** How a login reads: the pool's provider name, and the icon beside it. */
export const PROVIDER_META = {
    local: { label: 'Email & password', icon: 'pi-envelope' },
    google: { label: 'Google', icon: 'pi-google' },
    microsoft: { label: 'Microsoft', icon: 'pi-microsoft' }
}

export function providerLabel(provider) {
    return PROVIDER_META[provider]?.label ?? provider
}

export function providerIcon(provider) {
    return PROVIDER_META[provider]?.icon ?? 'pi-user'
}

// A Human carries the same three statuses as every administration record. A person who closes
// their own account retires their Human.
export const HUMAN_STATUS_META = {
    active: { label: 'Active', color: 'var(--p-status-success)' },
    suspended: { label: 'Suspended', color: 'var(--p-status-warning)' },
    retired: { label: 'Retired', color: 'var(--p-text-muted-color)' }
}

const HUMAN_STATUSES = Object.keys(HUMAN_STATUS_META)
const MACHINE_STATUSES = ['active', 'suspended', 'retired']
const PROVIDERS = Object.keys(PROVIDER_META)

const PROVIDER_ENUM_META = Object.fromEntries(PROVIDERS.map((provider) => [provider, { label: providerLabel(provider), color: null }]))

/**
 * The text a query names on one of `fields`, for the server's substring search: the first
 * clause on any of them, its wildcards dropped. An empty string when the query names none.
 * A broken query parses to diagnostics rather than throwing, and names nothing; the search bar
 * is already saying so.
 */
export function textClauseOf(query, fields) {
    for (const node of clausesOf(query)) {
        if (fields.includes(node.field) && typeof node.value?.text === 'string') {
            return node.value.text.replace(/[*?]/g, '').trim()
        }
    }
    return ''
}

/** The one value of `values` a query's `status:` clause names, or undefined for none or another. */
export function statusClauseOf(query, values) {
    for (const node of clausesOf(query)) {
        if (node.field === 'status' && values.includes(node.value?.text)) {
            return node.value.text
        }
    }
    return undefined
}

// The parser throws for a non-string only, which the `?? ''` rules out; anything else parses.
function clausesOf(query) {
    return flattenTop(FqlUtilities.parse(query ?? '')).filter((node) => node.type === 'clause')
}

/** A token's `iat`, `exp` and `lastUsedAt` are seconds; the formatter reads ISO text. */
export function isoFromSeconds(seconds) {
    const number = Number(seconds)
    if (seconds === null || seconds === undefined || seconds === '' || Number.isNaN(number)) {
        return null
    }
    return new Date(number * 1000).toISOString()
}

export const HUMANS = {
    fieldDefinitions: {
        fields: {
            name: { type: 'string' },
            primaryEmail: { type: 'string' },
            status: { type: 'enum', values: HUMAN_STATUSES },
            'logins.provider': { type: 'enum', values: PROVIDERS },
            'logins.email': { type: 'string' },
            deviceCount: { type: 'number' },
            closedAt: { type: 'string' }
        }
    },
    fieldMeta: {
        name: { label: 'Name' },
        primaryEmail: { label: 'Primary email' },
        status: { label: 'Status', enumMeta: HUMAN_STATUS_META },
        'logins.provider': { label: 'Logins', enumMeta: PROVIDER_ENUM_META },
        'logins.email': { label: 'Login email' },
        deviceCount: { label: 'Devices' },
        closedAt: { label: 'Closed at', date: true, searchHidden: true }
    },
    tableColumns: ['__primary', 'primaryEmail', 'status', 'logins.provider', 'deviceCount'],
    searchPlaceholder: 'Search humans — e.g. name:dana* or primaryEmail:dana@',
    searchHelp: 'Search by name or email with a clause, e.g. name:dana* — or use Filters.',
    hideNew: true,
    newRecord: null,
    // A person is never made here: access reaches them as an invitation they redeem.
    create() {
        return Promise.resolve(null)
    },
    listFilterOf(query) {
        return { q: textClauseOf(query, ['name', 'primaryEmail', 'logins.email']) }
    },
    rowOf(document) {
        return document
    },
    secondary(record) {
        return record.primaryEmail || ''
    }
}

export const MACHINES = {
    fieldDefinitions: {
        fields: {
            name: { type: 'string' },
            status: { type: 'enum', values: MACHINE_STATUSES },
            credentialCount: { type: 'number' },
            'credentials.clientId': { type: 'string' }
        }
    },
    fieldMeta: {
        name: { label: 'Name' },
        status: { label: 'Status', enumMeta: enumMetaFor('status', MACHINE_STATUSES) },
        credentialCount: { label: 'Credentials' },
        'credentials.clientId': { label: 'Client ID', searchHidden: true }
    },
    tableColumns: ['__primary', 'status', 'credentialCount'],
    searchPlaceholder: 'Active machines — ask for others with status:suspended or status:retired',
    searchHelp: 'Active programs unless a status: clause says otherwise, e.g. status:retired — or use Filters.',
    hideNew: false,
    newRecord() {
        return { name: 'New Machine' }
    },
    /** Answers the store's `{ machineId, clientId, clientSecret }`, or null when refused. */
    create(store, record) {
        return store.create(record.name)
    },
    // Active unless asked otherwise (ruled 2026-10-19): a retired Machine stays forever, the
    // same lifecycle as every other noun, so the list opens on the live ones and the person
    // asks for the rest by status.
    listFilterOf(query) {
        return { status: statusClauseOf(query, MACHINE_STATUSES) ?? 'active' }
    },
    // The count is a column; the server answers the credentials themselves.
    rowOf(document) {
        return { ...document, credentialCount: (document.credentials ?? []).length }
    },
    secondary(record) {
        const count = (record.credentials ?? []).length
        if (count === 1) {
            return '1 credential'
        }
        return `${count} credentials`
    }
}
